// Keep in sync with features/seo-listing/consts/SEO_ROUTE_PATTERNS.ts — import shared patterns.
import {
  CHARACTER_PERMALINK_PATTERN,
  FANDOM_CHARACTERS_HUB_PATTERN,
  FANDOM_PERMALINK_PATTERN,
  FANFIC_TAG_PERMALINK_PATTERN,
  ORIGINAL_TAG_PERMALINK_PATTERN,
  PAIRING_PERMALINK_PATTERN,
} from '@/features/seo-listing/consts';

export type ListingChromeKind =
  | 'catalog'
  | 'fandom'
  | 'fandom_character'
  | 'fandom_characters'
  | 'fandom_pairing'
  | 'tag';

export type ListingBreadcrumbSection = 'fanfics' | 'originals';

export const getListingSectionMeta = (
  section: ListingBreadcrumbSection = 'fanfics',
): { label: string; href: '/fanfics' | '/originals' } =>
  section === 'originals'
    ? { label: 'Ориджиналы', href: '/originals' }
    : { label: 'Фанфики', href: '/fanfics' };

export type ListingBreadcrumbItem = {
  label: string;
  href?: string;
};

export type CatalogEntityRef = {
  id?: string | number | null;
  slug?: string | null;
};

const DEFAULT_CATALOG_H1 = 'Фанфики';
const SEO_TEXT_COLLAPSE_LIMIT = 250;

export const getListingChromeKind = (pathname: string): ListingChromeKind => {
  if (FANDOM_CHARACTERS_HUB_PATTERN.test(pathname)) return 'fandom_characters';
  if (CHARACTER_PERMALINK_PATTERN.test(pathname)) return 'fandom_character';
  if (PAIRING_PERMALINK_PATTERN.test(pathname)) return 'fandom_pairing';
  if (FANDOM_PERMALINK_PATTERN.test(pathname)) return 'fandom';
  if (
    FANFIC_TAG_PERMALINK_PATTERN.test(pathname) ||
    ORIGINAL_TAG_PERMALINK_PATTERN.test(pathname)
  ) {
    return 'tag';
  }
  return 'catalog';
};

export const getFandomSlugFromPath = (pathname: string): string | null => {
  const charactersHub = FANDOM_CHARACTERS_HUB_PATTERN.exec(pathname);
  if (charactersHub) return charactersHub[1];
  const character = CHARACTER_PERMALINK_PATTERN.exec(pathname);
  if (character) return character[1];
  const pairing = PAIRING_PERMALINK_PATTERN.exec(pathname);
  if (pairing) return pairing[1];
  const fandom = FANDOM_PERMALINK_PATTERN.exec(pathname);
  if (fandom) return fandom[1];
  return null;
};

export const getFandomSlugFromPermanentUrl = (
  permanentUrl: string | null | undefined,
): string | null => {
  if (!permanentUrl) return null;
  const pathname = permanentUrl.split('?')[0] ?? '';
  return getFandomSlugFromPath(pathname);
};

export const buildFandomPermalinkHref = (fandomSlug: string): string =>
  `/fanfics/fandoms/${fandomSlug}`;

export const buildCharacterPermalinkHref = (fandomSlug: string, characterSlug: string): string =>
  `/fanfics/fandoms/${fandomSlug}/characters/${characterSlug}`;

export const buildPairingPermalinkHref = (fandomSlug: string, pairingSlug: string): string =>
  `/fanfics/fandoms/${fandomSlug}/pairings/${pairingSlug}`;

export const buildFanficTagPermalinkHref = (tagSlug: string): string => `/fanfics/tags/${tagSlug}`;

export const buildOriginalTagPermalinkHref = (tagSlug: string): string =>
  `/originals/tags/${tagSlug}`;

export const buildFandomCharactersHref = (
  fandomSlug: string,
  options: { page?: number; search?: string } = {},
): string => {
  const search = new URLSearchParams();
  const query = options.search?.trim() ?? '';
  if (query.length > 0) search.set('search', query);
  if (options.page && options.page > 1) search.set('page', String(options.page));
  const queryStr = search.toString();
  const basePath = `${buildFandomPermalinkHref(fandomSlug)}/characters`;
  return queryStr ? `${basePath}?${queryStr}` : basePath;
};

export const buildLegacyFilterHref = (input: {
  fandomId?: string;
  characterId?: string;
}): string => {
  const search = new URLSearchParams();
  if (input.fandomId) search.set('fandoms', input.fandomId);
  if (input.characterId) search.set('characters', input.characterId);
  const query = search.toString();
  return query.length > 0 ? `/fanfics?${query}` : '/fanfics';
};

/**
 * Fandom SEO on, character SEO off: keep the fandom permalink and pass the
 * character as a GET filter (same end state as selecting the character on the
 * fandom listing after soft SEO).
 */
export const buildFandomPermalinkCharacterQueryHref = (
  fandomSlug: string,
  characterId: string,
): string => {
  const search = new URLSearchParams();
  search.set('characters', characterId);
  return `${buildFandomPermalinkHref(fandomSlug)}?${search.toString()}`;
};

export const resolveCatalogEntityHref = (input: {
  fandomSlug?: string | null;
  fandomId?: string | null;
  characterSlug?: string | null;
  characterId?: string | null;
  permanentUrl?: string | null;
}): string | null => {
  // Character (or other entity) SEO page from API — never synthesize from slug.
  if (input.permanentUrl?.startsWith('/')) return input.permanentUrl;

  const fandomSlug = input.fandomSlug?.trim() || '';
  const characterId = input.characterId?.trim() || '';

  // On an SEO fandom tree (hub / fandom page) without character SEO: keep the
  // static fandom URL and append ?characters=id instead of dropping to catalog GET.
  if (fandomSlug.length > 0 && characterId.length > 0) {
    return buildFandomPermalinkCharacterQueryHref(fandomSlug, characterId);
  }

  if (input.fandomId || input.characterId) {
    return buildLegacyFilterHref({
      fandomId: input.fandomId ?? undefined,
      characterId: input.characterId ?? undefined,
    });
  }
  return null;
};

export const stripFandomPrefixFromPageName = (pageName: string, fandomTitle: string): string => {
  const name = pageName.trim();
  const fandom = fandomTitle.trim();
  if (name.length === 0 || fandom.length === 0) return name;
  const prefix = `${fandom} — `;
  return name.startsWith(prefix) ? name.slice(prefix.length).trim() : name;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const buildFandomListingH1 = (fandomTitle: string): string => `Фэндом «${fandomTitle}»`;

const buildEntityWithFandomListingH1 = (entityName: string, fandomTitle: string): string =>
  `${entityName} "${fandomTitle}"`;

const buildFandomCharactersListingH1 = (fandomTitle: string): string =>
  `Персонажи фэндома «${fandomTitle}»`;

const isBackendGeneratedFandomH1 = (h1: string, fandomTitle: string): boolean => {
  if (fandomTitle.length === 0) return false;
  if (h1 === fandomTitle || h1 === `"${fandomTitle}"` || h1 === `«${fandomTitle}»`) return true;
  return new RegExp(`^${escapeRegExp(fandomTitle)} \\+ \\d+$`).test(h1);
};

const collectGeneratedListingHeadings = (input: {
  fandomTitle: string;
  entityName: string;
  autoH1: string;
}): Set<string> => {
  const headings = new Set<string>([input.autoH1, DEFAULT_CATALOG_H1]);
  if (input.fandomTitle.length > 0) {
    headings.add(input.fandomTitle);
    headings.add(`Фэндом "${input.fandomTitle}"`);
    headings.add(buildFandomListingH1(input.fandomTitle));
    headings.add(`Персонажи "${input.fandomTitle}"`);
    headings.add(buildFandomCharactersListingH1(input.fandomTitle));
  }
  if (input.entityName.length > 0) headings.add(input.entityName);
  if (input.entityName.length > 0 && input.fandomTitle.length > 0) {
    headings.add(`${input.entityName} "${input.fandomTitle}"`);
    headings.add(buildEntityWithFandomListingH1(input.entityName, input.fandomTitle));
    headings.add(`${input.fandomTitle} + ${input.entityName}`);
  }
  return headings;
};

const resolveAutomaticListingHeading = (input: {
  kind: ListingChromeKind;
  fandomTitle: string;
  entityName: string;
  section?: ListingBreadcrumbSection;
}): string => {
  const catalogH1 = getListingSectionMeta(input.section).label;
  if (input.kind === 'fandom_characters') {
    return input.fandomTitle.length > 0
      ? buildFandomCharactersListingH1(input.fandomTitle)
      : catalogH1;
  }
  if (input.kind === 'tag') {
    return catalogH1;
  }
  if (input.kind === 'fandom_pairing') {
    return input.fandomTitle.length > 0 && input.entityName.length > 0
      ? buildEntityWithFandomListingH1(input.entityName, input.fandomTitle)
      : catalogH1;
  }
  if (input.fandomTitle.length > 0 && input.entityName.length > 0) {
    return buildEntityWithFandomListingH1(input.entityName, input.fandomTitle);
  }
  if (input.fandomTitle.length > 0) {
    return buildFandomListingH1(input.fandomTitle);
  }
  return catalogH1;
};

export const resolveListingHeading = (input: {
  kind: ListingChromeKind;
  resolvedH1?: string | null;
  fandomTitle?: string;
  entityName?: string;
  section?: ListingBreadcrumbSection;
}): string => {
  const fandomTitle = input.fandomTitle?.trim() ?? '';
  const entityName = input.entityName?.trim() ?? '';
  const autoH1 = resolveAutomaticListingHeading({
    kind: input.kind,
    fandomTitle,
    entityName,
    section: input.section,
  });
  const resolvedH1 = input.resolvedH1?.trim() ?? '';
  if (input.kind === 'fandom' && fandomTitle.length === 0 && resolvedH1.length > 0) {
    return isBackendGeneratedFandomH1(resolvedH1, resolvedH1)
      ? buildFandomListingH1(resolvedH1)
      : resolvedH1;
  }
  if (resolvedH1.length === 0) return autoH1;
  if (isBackendGeneratedFandomH1(resolvedH1, fandomTitle)) return autoH1;
  const generatedHeadings = collectGeneratedListingHeadings({
    fandomTitle,
    entityName,
    autoH1,
  });
  return generatedHeadings.has(resolvedH1) ? autoH1 : resolvedH1;
};

const LISTING_HEADING_LABELS = ['Фэндом', 'Персонажи'] as const;

export type ListingHeadingParts = {
  label: string | null;
  name: string;
};

export const splitListingHeading = (h1: string): ListingHeadingParts => {
  const heading = h1.trim();
  for (const label of LISTING_HEADING_LABELS) {
    const prefix = `${label} `;
    if (heading.startsWith(prefix)) {
      return { label, name: heading.slice(prefix.length) };
    }
  }
  return { label: null, name: heading };
};

const DEFAULT_CATALOG_DOCUMENT_TITLE = `${DEFAULT_CATALOG_H1} – читать фф онлайн бесплатно`;

export const buildListingDocumentTitle = (h1: string): string => {
  const heading = h1.trim();
  if (heading.length === 0 || heading === DEFAULT_CATALOG_H1) {
    return DEFAULT_CATALOG_DOCUMENT_TITLE;
  }
  return `${heading} – читать фф онлайн бесплатно`;
};

/**
 * Listing breadcrumbs. Last item is always current (no href).
 * Catalog is always `Главная / {секция}` — do not pass pageName as fandomTitle.
 */
export const buildListingBreadcrumbs = (input: {
  kind: ListingChromeKind;
  section?: ListingBreadcrumbSection;
  fandomTitle?: string;
  fandomHref?: string;
  entityName?: string;
}): ListingBreadcrumbItem[] => {
  const home = { label: 'Главная', href: '/' };
  const sectionMeta = getListingSectionMeta(input.section);
  const fandomTitle = input.fandomTitle?.trim() ?? '';
  const entityName = input.entityName?.trim() ?? '';

  if (input.kind === 'catalog') {
    // Character selected on catalog without fandom permalink → Секция / Имя
    if (entityName.length > 0 && fandomTitle.length === 0) {
      return [home, { label: sectionMeta.label, href: sectionMeta.href }, { label: entityName }];
    }
    return [home, { label: sectionMeta.label }];
  }

  const sectionCrumb = { label: sectionMeta.label, href: sectionMeta.href };
  if (input.kind === 'fandom_character' || input.kind === 'fandom_pairing') {
    const items: ListingBreadcrumbItem[] = [home, sectionCrumb];
    if (fandomTitle.length > 0) items.push({ label: fandomTitle, href: input.fandomHref });
    if (entityName.length > 0) items.push({ label: entityName });
    return items;
  }
  if (input.kind === 'fandom_characters') {
    const items: ListingBreadcrumbItem[] = [home, sectionCrumb];
    if (fandomTitle.length > 0) items.push({ label: fandomTitle, href: input.fandomHref });
    items.push({ label: 'Персонажи' });
    return items;
  }
  if (input.kind === 'tag') {
    const items: ListingBreadcrumbItem[] = [home, sectionCrumb];
    if (entityName.length > 0) items.push({ label: entityName });
    return items;
  }
  // fandom (and fallback)
  const items: ListingBreadcrumbItem[] = [home, sectionCrumb];
  if (fandomTitle.length > 0) items.push({ label: fandomTitle });
  return items;
};

export const buildListingBottomSeoHeading = (input: {
  kind: ListingChromeKind;
  fandomTitle?: string;
}): { eyebrow: string; heading: string } | null => {
  if (input.kind === 'catalog') {
    return {
      eyebrow: 'О Molwe',
      heading: 'Фанфики на Molwe — что здесь читают и пишут',
    };
  }
  if (
    input.kind !== 'fandom' &&
    input.kind !== 'fandom_character' &&
    input.kind !== 'fandom_pairing'
  ) {
    return null;
  }
  const fandomTitle = input.fandomTitle?.trim() ?? '';
  if (fandomTitle.length === 0) return null;
  return {
    eyebrow: 'О вселенной',
    heading: `Фанфики по «${fandomTitle}»`,
  };
};

/** Stable ru-RU grouping for SSR + browser (avoid locale engine drift in dev). */
export const formatListingCount = (count: number): string =>
  count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

export const formatCatalogFandomCount = (count: number): string => {
  const abs = Math.abs(count) % 100;
  const last = abs % 10;
  const noun = abs > 10 && abs < 20 ? 'фэндомах' : last === 1 ? 'фэндоме' : 'фэндомах';
  return `${formatListingCount(count)} ${noun}`;
};

export const formatCatalogCharacterCount = (count: number, suffix?: string): string => {
  const abs = Math.abs(count) % 100;
  const last = abs % 10;
  const noun =
    abs > 10 && abs < 20
      ? 'персонажей'
      : last > 1 && last < 5
        ? 'персонажа'
        : last === 1
          ? 'персонаж'
          : 'персонажей';
  const label = `${formatListingCount(count)} ${noun}`;
  const extra = suffix?.trim() ?? '';
  return extra.length > 0 ? `${label} ${extra}` : label;
};

export const shouldCollapseSeoText = (text: string): boolean =>
  text.trim().length > SEO_TEXT_COLLAPSE_LIMIT;

export const getSeoTextCollapseLimit = (): number => SEO_TEXT_COLLAPSE_LIMIT;

const FANDOM_CHARACTERS_HUB_SITEMAP_PATTERN = /^\/fanfics\/fandoms\/[^/]+\/characters\/?$/;

export const isCatalogSitemapUrl = (permanentUrl: string): boolean => {
  if (!permanentUrl.startsWith('/')) return false;
  if (FANDOM_CHARACTERS_HUB_SITEMAP_PATTERN.test(permanentUrl)) return false;
  return (
    permanentUrl.startsWith('/fanfics/fandoms/') ||
    permanentUrl.startsWith('/fanfics/tags/') ||
    permanentUrl.startsWith('/originals/tags/') ||
    permanentUrl === '/fanfics/fandoms' ||
    permanentUrl === '/fanfics/characters'
  );
};

export const toEntityId = (value: CatalogEntityRef['id']): string => {
  if (value == null) return '';
  return String(value).trim();
};
